/**
 * Plugin de runtime do CKEditor que representa um **trecho fixo** do template
 * (tabela com `{{#each}}`, `{{#if}}`/`{{/if}}`, imagem, comentário HTML...) como
 * um widget inline atômico.
 *
 * O CKEditor não sabe representar esses trechos — descarta atributos, arranca o
 * `{{#each}}` de dentro do `<tbody>` e remove o que não está no schema. Por isso
 * o `TemplateBodyLockService` os recorta ANTES de o HTML chegar ao editor e
 * deixa no lugar só um marcador vazio com o número do trecho:
 *
 *   <span data-cred-block="0" data-cred-block-label="Tabela: Contratos"></span>
 *
 * O editor só carrega o marcador de um lado para o outro; o HTML original volta
 * byte a byte no `merge` do serviço.
 *
 * Mesmas restrições do `template-variable.plugin.ts`: registrado via
 * `config.extraPlugins` e sem importar nada de `@ckeditor/*`.
 */

export const TEMPLATE_BLOCK_ELEMENT = 'templateBlock';
export const TEMPLATE_BLOCK_DATA_ATTRIBUTE = 'data-cred-block';
export const TEMPLATE_BLOCK_LABEL_ATTRIBUTE = 'data-cred-block-label';
export const TEMPLATE_BLOCK_CLASS = 'cred-block';

/**
 * `preview` devolve o HTML de exibição do trecho (`previews` do
 * TemplateBodyLockService). Só muda o que o usuário vê: o `dataDowncast` segue
 * gravando o marcador, e o save repõe o HTML original.
 */
export function createTemplateBlockPlugin(
  preview: (blockId: number) => string | null = () => null
): any {
  return class TemplateBlockPlugin {
    editor: any;

    constructor(editor: any) {
      this.editor = editor;
    }

    init(): void {
      this.editor.model.schema.register(TEMPLATE_BLOCK_ELEMENT, {
        inheritAllFrom: '$inlineObject',
        allowAttributes: ['blockId', 'label']
      });

      const conversion = this.editor.conversion;

      // Prioridade alta: o GeneralHtmlSupport também faz upcast de `span`.
      conversion.for('upcast').elementToElement({
        view: {
          name: 'span',
          attributes: { [TEMPLATE_BLOCK_DATA_ATTRIBUTE]: true }
        },
        model: (viewElement: any, { writer }: any) =>
          writer.createElement(TEMPLATE_BLOCK_ELEMENT, {
            blockId: viewElement.getAttribute(TEMPLATE_BLOCK_DATA_ATTRIBUTE),
            label: viewElement.getAttribute(TEMPLATE_BLOCK_LABEL_ATTRIBUTE) ?? ''
          }),
        converterPriority: 'high'
      });

      conversion.for('dataDowncast').elementToElement({
        model: TEMPLATE_BLOCK_ELEMENT,
        view: (modelElement: any, { writer }: any) =>
          writer.createRawElement('span', {
            [TEMPLATE_BLOCK_DATA_ATTRIBUTE]: modelElement.getAttribute('blockId'),
            [TEMPLATE_BLOCK_LABEL_ATTRIBUTE]: modelElement.getAttribute('label')
          })
      });

      conversion.for('editingDowncast').elementToElement({
        model: TEMPLATE_BLOCK_ELEMENT,
        view: (modelElement: any, { writer }: any) => {
          const label = modelElement.getAttribute('label') || 'Trecho fixo';
          const html = preview(Number(modelElement.getAttribute('blockId')));

          // `null`: rótulo 🔒. `''`: nada visível. Senão, o conteúdo do trecho.
          const variant =
            html === null
              ? ''
              : html === ''
              ? ` ${TEMPLATE_BLOCK_CLASS}--hidden`
              : /<(table|figure|hr|p|div|h[1-6]|ul|ol)\b/i.test(html)
              ? ` ${TEMPLATE_BLOCK_CLASS}--preview ${TEMPLATE_BLOCK_CLASS}--block`
              : ` ${TEMPLATE_BLOCK_CLASS}--preview`;

          const span = writer.createRawElement(
            'span',
            {
              class: `${TEMPLATE_BLOCK_CLASS}${variant} ck-widget`,
              contenteditable: 'false',
              title: `${label} — trecho fixo do template, não pode ser editado aqui`
            },
            (domElement: HTMLElement) => {
              if (html) {
                domElement.appendChild(renderPreview(html));
              } else {
                domElement.textContent = label;
              }
            }
          );

          writer.setCustomProperty('widget', true, span);
          // Lista, não função — ver template-variable.plugin.ts.
          writer.setCustomProperty('widgetLabel', [label], span);

          return span;
        }
      });
    }
  };
}

/**
 * HTML do template -> nós para exibir. O `<template>` não executa script nem
 * carrega nada ao fazer o parse; mesmo assim sai o que poderia rodar código ao
 * entrar na página (`<script>`, `on*=`, `javascript:`).
 */
function renderPreview(html: string): DocumentFragment {
  const template = document.createElement('template');
  template.innerHTML = html;

  template.content
    .querySelectorAll('script, iframe, object, embed, link, meta, style, base')
    .forEach((node) => node.remove());

  template.content.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      if (
        /^on/i.test(attribute.name) ||
        /^\s*javascript:/i.test(attribute.value)
      ) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  return template.content;
}
